import React from "react";
import { Tag, Tooltip } from "antd";
import {
  CHART_COLORS,
  fmtINR,
  fmtINRCompact,
  fmtINRSigned,
} from "./executionShared";

// Shared chart chrome + cell renderers for the Monthly Execution charts.
// The dashboard cards (ExecutionCharts), the expanded analysis modals
// (ExecutionChartAnalysis) and the month drill panels (ExecutionMonthDrill) all
// render the same grammar — one ₹ axis, hairline grid, light axis ink, values on
// hover — so the axis/tooltip/legend objects and the money cells live here
// instead of being re-declared per file.

export const AXIS = {
  axisLine: { show: false },
  axisTick: { show: false },
  axisLabel: { color: CHART_COLORS.axisLabel, fontSize: 11 },
};

export const MONEY_AXIS = {
  type: "value" as const,
  ...AXIS,
  axisLabel: {
    ...AXIS.axisLabel,
    formatter: (value: number) => fmtINRCompact(value),
  },
  splitLine: { lineStyle: { color: CHART_COLORS.grid } },
};

// Category axis for the horizontal (bar-on-y) drill charts: no split lines on
// the category side, labels kept short by the caller.
export const CATEGORY_AXIS = {
  type: "category" as const,
  ...AXIS,
};

export const TOOLTIP = {
  trigger: "axis" as const,
  axisPointer: { type: "shadow" as const, shadowStyle: { color: "rgba(148,163,184,0.08)" } },
  borderColor: "#e8edf4",
  textStyle: { fontSize: 12 },
  valueFormatter: (value: any) =>
    value === null || value === undefined ? "—" : fmtINRCompact(Number(value)),
};

export const LEGEND = {
  bottom: 0,
  itemWidth: 14,
  itemHeight: 8,
  itemGap: 16,
  textStyle: { color: "#64748b", fontSize: 11 },
};

// ------------------------------------------------------------------ cells

export const mutedDash = <span style={{ color: "#c9d2e3" }}>—</span>;

export const moneyCell = (value: string | number, bold = false) => (
  <Tooltip title={fmtINR(value)}>
    <span
      style={{
        fontVariantNumeric: "tabular-nums",
        fontWeight: bold ? 600 : 400,
        color: bold ? "#022c57" : undefined,
      }}
    >
      {fmtINRCompact(value)}
    </span>
  </Tooltip>
);

export const signedCell = (value: number, flip = false) => {
  const bad = flip ? value < 0 : value > 0;
  return (
    <Tooltip title={fmtINRSigned(value, true)}>
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          color: value === 0 ? "#8c94a8" : bad ? CHART_COLORS.negative : CHART_COLORS.positive,
        }}
      >
        {fmtINRSigned(value)}
      </span>
    </Tooltip>
  );
};

// Earned ₹ in a drill grid: real money in ink, one muted word until actuals
// land (same grammar as MonthDetailPage's earnedCell).
export const earnedMoneyCell = (value: string | number) =>
  Number(value) === 0 ? (
    <Tooltip title="No actuals captured yet">
      <span style={{ color: "#a9b6c9" }}>pending</span>
    </Tooltip>
  ) : (
    moneyCell(value, true)
  );

// Deviation convention (matches MonthDetailPage / PlannedVsActualTree): quiet
// dash while nothing is executed, red when over the plan, green when under.
export const deviationMoneyCell = (deviation: number, earned: string | number) => {
  if (Number(earned) === 0) {
    return (
      <Tooltip title="No executed value yet for this row">
        <span style={{ color: "#c9d2e3" }}>—</span>
      </Tooltip>
    );
  }
  return signedCell(deviation);
};

// ---------------------------------------------------------------- insights

export interface Insight {
  title: string;
  body: React.ReactNode;
  tone?: "ok" | "warn" | "neutral";
}

const toneDot: Record<string, string> = {
  ok: CHART_COLORS.actual,
  warn: CHART_COLORS.backlog,
  neutral: "#94a3b8",
};

export const InsightsRail: React.FC<{ insights: Insight[] }> = ({ insights }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.6,
        color: "#8094ae",
      }}
    >
      INSIGHTS
    </div>
    {insights.map((insight, index) => (
      <div
        key={index}
        style={{
          background: "#f8fafc",
          border: "1px solid #eef1f6",
          borderRadius: 8,
          padding: "8px 12px",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#022c57",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: toneDot[insight.tone || "neutral"],
              flexShrink: 0,
            }}
          />
          {insight.title}
        </div>
        <div style={{ fontSize: 12, color: "#526484", marginTop: 2, lineHeight: 1.5 }}>
          {insight.body}
        </div>
      </div>
    ))}
  </div>
);

// Series-toggle chips (checkable, colored dot per series).
export const SeriesToggles: React.FC<{
  options: Array<{ key: string; label: string; color: string }>;
  value: string[];
  onChange: (next: string[]) => void;
}> = ({ options, value, onChange }) => (
  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
    {options.map((option) => {
      const checked = value.includes(option.key);
      return (
        <Tag.CheckableTag
          key={option.key}
          checked={checked}
          onChange={(next) =>
            onChange(next ? [...value, option.key] : value.filter((k) => k !== option.key))
          }
          style={{
            border: `1px solid ${checked ? option.color : "#e2e8f0"}`,
            background: checked ? "#f8fafc" : "#fff",
            color: checked ? "#022c57" : "#8094ae",
            paddingInline: 10,
            userSelect: "none",
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: checked ? option.color : "#e2e8f0",
              marginRight: 6,
            }}
          />
          {option.label}
        </Tag.CheckableTag>
      );
    })}
  </div>
);

// trend over the actualized window: compare the mean of the two halves.
export const trendOf = (values: number[]): "up" | "down" | "flat" => {
  if (values.length < 4) return "flat";
  const half = Math.floor(values.length / 2);
  const first = values.slice(0, half).reduce((a, b) => a + b, 0) / half;
  const second = values.slice(-half).reduce((a, b) => a + b, 0) / half;
  if (first === 0) return "flat";
  const change = (second - first) / Math.abs(first);
  return change > 0.05 ? "up" : change < -0.05 ? "down" : "flat";
};

// ECharts series click → the clicked category index, or null for anything that
// isn't a data mark (legend, axis, empty canvas).
export const seriesClickIndex = (params: any): number | null =>
  params && params.componentType === "series" && typeof params.dataIndex === "number"
    ? params.dataIndex
    : null;

// Every drillable series carries the same affordance.
export const CLICKABLE = { cursor: "pointer" } as const;
